import { AcceptCookieBanner } from '@atoms/AcceptCookie';
import { CreditUsageNotification } from '@atoms/CreditUsageNotification';
import { MonitoringNotification } from '@atoms/Monitoring/MonitoringNotification';
import { useIsCopilotEnabled } from '@common/Copilot/useIsCopilotEnabled';
import { StyledDowngradedToStarterBanner } from '@common/credits/ProfessionalEdition/DowngradedToStarterBanner';
import { HackthonBanner } from '@common/HackathonBanner';
import { PageHeader } from '@common/Header/PageHeader';
import { useHasOnlyAnalystFabric, useOnboardingDetail } from '@common/onboarding/hooks';
import { getSparkOnboardingFlow, getSqlOnboardingFlow } from '@common/onboarding/ide-utils';
import { OnboardingStatus } from '@common/onboarding/types';
import { UserPreferenceKeys } from '@common/preferences/types';
import { useUserPreference } from '@common/preferences/userPreference';
import { useOnboardingDetailReplayQuery } from '@common/Replay/api';
import { getSqlIDEUrl, Private_Routes, Public_Routes } from '@common/url';
import { InactiveUserDialog, RejectedUserDialog } from '@common/UserStatus';
import { isDeploymentUXEnabled } from '@common/utils/hooks/flags';
import DiscourseOauth from '@pages/auth/DiscourseOauth';
import DiscourseSSO from '@pages/auth/DiscourseSSO';
import DeploymentPage from '@pages/Deployment/Deployment';
import { MaintenanceUpgradeStatusList } from '@pages/Maintenance/constants';
import { MaintenanceNotification } from '@pages/Maintenance/MaintenanceNotification';
import { ServiceDownNotification } from '@pages/Maintenance/ServiceDownNotification';
import { useDownServiceStatus } from '@pages/Maintenance/util';
import { OnboardingHome } from '@pages/onboarding';
import { UserIndustry } from '@pages/onboarding/constants';
import { Onboarding } from '@pages/onboarding/Onboarding';
import { HUB_URL } from '@pages/PackageHub/constants';
import { CreateTeam } from '@pages/Team/CreateTeam';
import { ProfessionalEditionHeader } from '@pages/UserSettings/UsageAndBilling/ProfessionalEdition/components/ProfessionalEditionHeader';
import { InvitationStatus } from '@prophecy/graphql-types/enums';
import { MetadataIcon, PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { MetadataPageContainer, Stack, TypographyGlobalStyle } from '@prophecy/ui-v3';
import { BASE_PATH, history, useLocationPathName } from '@prophecy/utils/history';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { withSentryReactRouterV6Routing } from '@sentry/react';
import { isEmpty } from 'lodash-es';
import {
  matchPath,
  Navigate,
  Outlet,
  Route,
  Routes,
  unstable_HistoryRouter as HistoryRouter,
  useParams
} from 'react-router-dom';
import styled from 'styled-components';

import {
  AppListPage,
  ComponentListPage,
  CreateEntityPage,
  CreateFabricPage,
  CreateProject,
  DatasetDetailPage,
  GemBuilderPage,
  HomePage,
  IdeHome,
  IDEPage,
  JobDetailPage,
  LineagePage,
  LineageSearchPage,
  Maintenance,
  OAuthCallback,
  OAuthSamlCallback,
  PackageDetail,
  PackageHubListPage,
  PipelineDetailPage,
  PrivateMainSection,
  ProjectDetailPage,
  ServiceDown,
  SqlPage,
  SubgraphDetailPage,
  TeamDetailPage,
  TranspilerDetailPage,
  TranspilerImportPage,
  TranspilerListingPage,
  UpdateFabricPage,
  UpgradePlanPage,
  UserDetailPage,
  UserSettings,
  Harmonization
} from '../App';
import { useAppMetadata } from '../context/appMetadata';
import { useGlobalVariables } from '../context/globalConfig/securedConfigHook';
import { useUnsecuredGlobalVariables } from '../context/globalConfig/unsecuredConfigHook';
import { useTabFocusEvents } from '../context/hooks/useTabFocusEvents';
import { UserFabricStatusKnownStatus, UserState } from '../context/types';
import { useUser } from '../context/user';
import { SideNavBar } from '../layout/SideNavBar';
import { useDelightSurvey } from '../trackers/customerDelight';
import useWebsocketLogs from '../utils/useWebsocketDebug';
import { getPageTitle, NoMatch, StyledRoot } from './common';

const RouteTemplate = () => {
  const { user } = useUser();

  const sqlFabricStatus = user.sqlFabricStatus;
  const preLoginData = useAppMetadata();
  const [newUserSignupAutoProjectClone] = useUserPreference(UserPreferenceKeys.newUserSignupAutoProjectClone);
  const globalConfigManager = useGlobalVariables();
  const unsecureGlobalConfigManager = useUnsecuredGlobalVariables();
  const updateStatus = preLoginData?.maintenanceUpdateInfo?.upgrade_status?.status;
  const userInfo = preLoginData?.user;
  const isUnderMaintenance = updateStatus && MaintenanceUpgradeStatusList.indexOf(updateStatus) !== -1;
  const sparkFabricStatus = userInfo?.fabric;
  const isFabricSet = sparkFabricStatus === UserFabricStatusKnownStatus.AlreadyHaveFabric;
  const trialInfo = userInfo?.trialInfo;
  const isCopilotEnabled = useIsCopilotEnabled();
  let isOnboardingRequired = false;
  let onboardingUrl = Private_Routes.Onboarding.getUrl();
  const sparkOnboardingStatus = globalConfigManager.getSparkOnboardingStatus;
  const sqlOnboardingStatus = globalConfigManager.getSqlOnboardingStatus;
  const sparkOnboardingPipelineId = userInfo?.onboardingDetail?.project?.pipelines[0]?._id;
  const sqlOnboardingProjectId = userInfo?.onboardingDetail?.sqlProject?._id;

  const hasOnlyAnalystFabric = useHasOnlyAnalystFabric();

  const sparkOnboarding = sparkOnboardingPipelineId
    ? getSparkOnboardingFlow(
        sparkFabricStatus as number,
        sparkOnboardingStatus,
        sparkOnboardingPipelineId,
        hasOnlyAnalystFabric
      )
    : null;
  const sqlOnboarding = sqlOnboardingProjectId
    ? getSqlOnboardingFlow(sqlFabricStatus, sqlOnboardingStatus, sqlOnboardingProjectId)
    : null;
  const isSqlOnboardingPending = sqlOnboarding && sqlOnboarding.flow === OnboardingStatus.ONBOARDING_PIPELINE;
  const isSparkOnboardingPending = sparkOnboarding && sparkOnboarding.flow === OnboardingStatus.ONBOARDING_PIPELINE;

  const isCreditsEnabled = globalConfigManager.isCreditsEnabled;
  const isOnboardingV2Enabled = globalConfigManager.isOnboardingV2Enabled;
  const isProfessionalEditionEnabled = globalConfigManager.isProfessionalEditionEnabled;
  const userOnboardingDetails = globalConfigManager.getUserOnboardingDetails;
  const userProfession = userOnboardingDetails?.profession || userInfo?.userOnboardingDetails?.profession;
  const userIndustry = userOnboardingDetails?.industry || userInfo?.userOnboardingDetails?.industry;
  const { defaultOnboardingProjectId } = useOnboardingDetailReplayQuery({
    enabled: isProfessionalEditionEnabled
  });

  const analystFabric = userInfo?.onboardingDetail?.analystFabric;

  // If analystFabric is present, and profession is empty and user has not skipped onboarding v2, then we consider onboarding v2 as required
  const isOnboardingV2Required = Boolean(analystFabric) && isEmpty(userProfession);
  if (isOnboardingV2Enabled && !isProfessionalEditionEnabled) {
    const analystProjectId = userInfo?.onboardingDetail?.analystProject?._id || globalConfigManager.getAnalystProjectId;

    if (analystProjectId) {
      onboardingUrl = getSqlIDEUrl({
        uid: analystProjectId
      });
    } else if (userIndustry === UserIndustry.HOSPITALITY || userIndustry === UserIndustry.OTHER) {
      onboardingUrl = HUB_URL.TEMPLATE;
    } else {
      onboardingUrl = Private_Routes.Onboarding.getUrl();
    }
  } else if (!isProfessionalEditionEnabled) {
    if (isCopilotEnabled) {
      if (isSparkOnboardingPending && isSqlOnboardingPending) {
        isOnboardingRequired = true;
      }
    } else {
      if (isSparkOnboardingPending && sparkOnboarding) {
        isOnboardingRequired = true;
        onboardingUrl = sparkOnboarding.url;
      }
    }
  }
  const enterpriseTrial = Boolean(trialInfo && !trialInfo?.billingStarted);
  const expiryTime: number = trialInfo?.daysLeft || 2;
  const isPlanExpired = Boolean(unsecureGlobalConfigManager.isMeteringEnabled && enterpriseTrial && expiryTime < 1);

  globalConfigManager.setFabricStatus(isFabricSet);
  globalConfigManager.setUnsetFabricStatusId(sparkFabricStatus as number);

  const patterns = [
    '/create/*',
    '/entity/*',
    '/settings/*',
    '/lineage/*',
    '/deployment/*',
    '/pipeline/*',
    '/job/*',
    '/components/*'
  ];

  const pathname = useLocationPathName();
  const routeMatched = patterns.some((pattern) => {
    const match = matchPath(pattern, pathname);
    if (match) {
      return true;
    }
    return false;
  });

  const url = new URL(window.location.href);
  const isRemix = url.searchParams.get('remix') === 'true';

  const isProphecyDown = useDownServiceStatus()?.isProphecyDown;
  useTabFocusEvents();

  let OutletComponent = Outlet;
  const pathName = window.location.pathname;
  const homeUrl = Private_Routes.Home.getUrl();
  const isHomePageUrl = pathName === homeUrl || pathName === '/metadata' || pathName === '/metadata/';

  const isNewUserSignup = isProfessionalEditionEnabled && !newUserSignupAutoProjectClone && defaultOnboardingProjectId;
  const { uid: projectId } = useParams();
  //Check for default projectId
  const isDifferentProject = projectId && defaultOnboardingProjectId && projectId !== defaultOnboardingProjectId;
  //Maintenance page should get the priority than prophecydown
  if (isUnderMaintenance && (pathname !== Public_Routes.Maintenance.getUrl() || routeMatched)) {
    OutletComponent = () => <Navigate replace to={Public_Routes.Maintenance.getUrl()} />;
  } else if (
    isProphecyDown &&
    !isUnderMaintenance &&
    (pathname !== Public_Routes.ProphecyDown.getUrl() || routeMatched)
  ) {
    OutletComponent = () => <Navigate replace to={Public_Routes.ProphecyDown.getUrl()} />;
  } else if (isPlanExpired && (pathname === Private_Routes.Home.getUrl() || routeMatched)) {
    OutletComponent = () => <Navigate replace to={Private_Routes.UPGRADE_PLAN.getUrl()} />;
  } else if (!isUnderMaintenance && pathname === Public_Routes.Maintenance.getUrl()) {
    OutletComponent = () => <Navigate replace to={Private_Routes.Home.getUrl()} />;
  } else if (isProfessionalEditionEnabled && (isHomePageUrl || isDifferentProject) && isNewUserSignup && !isRemix) {
    OutletComponent = () => (
      <Navigate replace to={Private_Routes.SQL_IDE.getUrl({ uid: defaultOnboardingProjectId })} />
    );
  } else if (!isHomePageUrl && isNewUserSignup && isDifferentProject && !isRemix) {
    // TODO: Remove this once in next release. doesn't seems to be needed.
    OutletComponent = () => <Navigate replace to={Private_Routes.Home.getUrl()} />;
  } else if (
    !isRemix &&
    ((isOnboardingRequired && onboardingUrl) ||
      (isOnboardingV2Required &&
        ![
          onboardingUrl,
          sparkOnboarding?.url || '',
          sqlOnboarding?.url.split('?')[0],
          Private_Routes.Settings.home.getUrl()
        ].some((item) => pathname.startsWith(item as string)) &&
        !isProfessionalEditionEnabled))
  ) {
    OutletComponent = () => <Navigate replace to={onboardingUrl} />;
  }

  return (
    <>
      <DocumentTitle title={getPageTitle()} />
      {!isUnderMaintenance && (
        <>
          <ServiceDownNotification />
          <MaintenanceNotification />
          <MonitoringNotification />
          {isCreditsEnabled && <CreditUsageNotification />}
        </>
      )}
      <StyledRoot>
        <Stack width='100%' direction='horizontal'>
          {!isProphecyDown && !isUnderMaintenance && (
            <SideNavBar
              isOnboardingRequired={isOnboardingRequired && !isProfessionalEditionEnabled}
              isPlanExpired={isPlanExpired}
            />
          )}
          <OutletComponent />
        </Stack>
      </StyledRoot>
    </>
  );
};

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

function UserSettingsWrapper({ isProfessionalEditionEnabled }: { isProfessionalEditionEnabled: boolean }) {
  if (isProfessionalEditionEnabled) {
    return (
      <Stack direction='vertical' width='100%'>
        <OverflowContainer>
          <PrivateMainSection wrapInLayout={false}>
            <ProfessionalEditionHeader />
            <MetadataPageContainer background='inherit' main={<UserSettings />}></MetadataPageContainer>
          </PrivateMainSection>
        </OverflowContainer>
      </Stack>
    );
  }

  return (
    <PrivateMainSection wrapInLayout={true}>
      <UserSettings />
    </PrivateMainSection>
  );
}

export const PrivateRoutes = function () {
  const globalConfigManager = useGlobalVariables();
  const user = useUser();
  const { user: userData } = useAppMetadata();

  useDelightSurvey();
  useWebsocketLogs();

  const isOnboardingV2Enabled = globalConfigManager.isOnboardingV2Enabled;
  const { analystFabric } = useOnboardingDetail();
  const isProfessionalEditionEnabled = globalConfigManager.isProfessionalEditionEnabled;
  const shouldRedirectToOnboardingV2 = Boolean(isOnboardingV2Enabled && analystFabric);

  const checkUserState = globalConfigManager.isCreditsEnabled;
  const isInActiveUser = checkUserState && userData?.userState === UserState.inactive;
  const isRejectedUser =
    checkUserState &&
    (userData?.userState === UserState.rejected ||
      [InvitationStatus.rejected, InvitationStatus.suspended].includes(userData?.invitationStatus as InvitationStatus));

  const defaultRoute = (
    <Route
      path='*'
      element={isInActiveUser ? <InactiveUserDialog /> : isRejectedUser ? <RejectedUserDialog /> : <NoMatch />}
    />
  );

  return (
    <>
      <TypographyGlobalStyle />
      <AcceptCookieBanner />
      <HackthonBanner />
      <HistoryRouter basename={BASE_PATH} history={history}>
        <SentryRoutes>
          <Route element={<RouteTemplate />}>
            <Route
              key={Public_Routes.OauthCallback.url}
              path={Public_Routes.OauthCallback.url}
              element={
                <PrivateMainSection>
                  <OAuthCallback />
                </PrivateMainSection>
              }
            />

            <Route
              key={Public_Routes.OauthSamlCallback.url}
              path={Public_Routes.OauthSamlCallback.url}
              element={
                <PrivateMainSection>
                  <OAuthSamlCallback />
                </PrivateMainSection>
              }
            />

            <Route
              key={Private_Routes.DiscourseSSO.url}
              path={Private_Routes.DiscourseSSO.url}
              element={
                <PrivateMainSection>
                  <DiscourseSSO />
                </PrivateMainSection>
              }
            />

            <Route
              key={Private_Routes.DiscourseOauth.url}
              path={Private_Routes.DiscourseOauth.url}
              element={
                <PrivateMainSection>
                  <DiscourseOauth />
                </PrivateMainSection>
              }
            />

            {!isInActiveUser && !isRejectedUser && (
              <>
                <Route
                  key={Private_Routes.Home.url}
                  path={Private_Routes.Home.url}
                  element={
                    <PrivateMainSection>
                      <StyledDowngradedToStarterBanner />
                      <HomePage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  key={Public_Routes.Maintenance.url}
                  path={Public_Routes.Maintenance.url}
                  element={
                    <PrivateMainSection wrapInLayout={true}>
                      <Maintenance />
                    </PrivateMainSection>
                  }
                />
                <Route
                  key={Public_Routes.ProphecyDown.url}
                  path={Public_Routes.ProphecyDown.url}
                  element={
                    <PrivateMainSection wrapInLayout={true}>
                      <ServiceDown />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Harmonization.url}
                  key={`Harmonization`}
                  element={
                    <PrivateMainSection wrapInLayout={false}>
                      <Harmonization />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Create_Entity.home.url}
                  key={`CreateEntityHome`}
                  element={
                    <PrivateMainSection
                      wrapInLayout={true}
                      header={
                        <PageHeader
                          crumbs={[{ label: 'Create Entity', icon: <PlusIcon type='default' /> }]}
                          noWrap={true}
                        />
                      }>
                      <CreateEntityPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Create_Entity.project.url}
                  key={`CreateProject`}
                  element={
                    <PrivateMainSection>
                      <CreateProject />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Create_Entity.fabric.new.url}
                  key={`CreateFabric`}
                  element={
                    <PrivateMainSection>
                      <CreateFabricPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Create_Entity.fabric.existing.url}
                  key={`CreateFabric`}
                  element={
                    <PrivateMainSection>
                      <CreateFabricPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Create_Entity.tab.url}
                  key={`CreateEntityTab`}
                  element={
                    <PrivateMainSection wrapInLayout={true}>
                      <CreateEntityPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Create_Entity.team.url}
                  key={`CreateTeam`}
                  element={
                    <PrivateMainSection>
                      <CreateTeam />
                    </PrivateMainSection>
                  }
                />
                {globalConfigManager.isPlaygroundEnabled && (
                  <>
                    <Route
                      path={Private_Routes.Gems.url}
                      key={Private_Routes.Gems.url}
                      element={
                        <PrivateMainSection wrapInLayout={true}>
                          <ComponentListPage />
                        </PrivateMainSection>
                      }
                    />
                    <Route
                      path={Private_Routes.GemPlayground.url}
                      key={Private_Routes.GemPlayground.url}
                      element={
                        <PrivateMainSection>
                          <GemBuilderPage isAirflow={false} />
                        </PrivateMainSection>
                      }
                    />
                    <Route
                      path={Private_Routes.AirflowGemPlayground.url}
                      key={Private_Routes.AirflowGemPlayground.url}
                      element={
                        <PrivateMainSection>
                          <GemBuilderPage isAirflow />
                        </PrivateMainSection>
                      }
                    />
                  </>
                )}
                <Route
                  path={Private_Routes.UPGRADE_PLAN.url}
                  key={Private_Routes.UPGRADE_PLAN.url}
                  element={
                    <PrivateMainSection wrapInLayout={true}>
                      <UpgradePlanPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Package.home.url}
                  key={Private_Routes.Package.home.url}
                  element={
                    <PrivateMainSection>
                      <PackageDetail />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Package.tab.url}
                  key={Private_Routes.Package.tab.url}
                  element={
                    <PrivateMainSection>
                      <PackageDetail />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.PackageHubList.url}
                  key={Private_Routes.PackageHubList.url}
                  element={
                    <PrivateMainSection>
                      <PackageHubListPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.User.home.url}
                  key={Private_Routes.User.home.url}
                  element={
                    <PrivateMainSection
                      wrapInLayout={true}
                      header={
                        <PageHeader
                          crumbs={[{ label: 'Metadata', icon: <MetadataIcon type='extended' /> }]}
                          noWrap={true}
                        />
                      }>
                      <UserDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.User.tab.url}
                  key={Private_Routes.User.tab.url}
                  element={
                    <PrivateMainSection
                      wrapInLayout={true}
                      header={
                        <PageHeader
                          crumbs={[{ label: 'Metadata', icon: <MetadataIcon type='extended' /> }]}
                          noWrap={true}
                        />
                      }>
                      <UserDetailPage />
                    </PrivateMainSection>
                  }
                />

                <Route
                  path={Private_Routes.Lineage_IDE.datasetOrPipeline.url}
                  key={`datasetOrPipeline_lineage`}
                  element={
                    <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
                      <LineagePage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Lineage_IDE.column.url}
                  key={`column_lineage`}
                  element={
                    <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
                      <LineagePage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Lineage_Search.url}
                  key={Private_Routes.Lineage_Search.url}
                  element={
                    <PrivateMainSection>
                      <LineageSearchPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Team.home.url}
                  key={Private_Routes.Team.home.url}
                  element={
                    <PrivateMainSection>
                      <TeamDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Team.tab.url}
                  key={Private_Routes.Team.tab.url}
                  element={
                    <PrivateMainSection>
                      <TeamDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Subgraph.home.url}
                  key={Private_Routes.Subgraph.home.url}
                  element={
                    <PrivateMainSection>
                      <SubgraphDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Subgraph.tab.url}
                  key={Private_Routes.Subgraph.tab.url}
                  element={
                    <PrivateMainSection>
                      <SubgraphDetailPage />
                    </PrivateMainSection>
                  }
                />

                <Route
                  path={Private_Routes.Dataset.home.url}
                  key={Private_Routes.Dataset.home.url}
                  element={
                    <PrivateMainSection>
                      <DatasetDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Dataset.tab.url}
                  key={Private_Routes.Dataset.tab.url}
                  element={
                    <PrivateMainSection>
                      <DatasetDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Job.home.url}
                  key={Private_Routes.Job.home.url}
                  element={
                    <PrivateMainSection>
                      <JobDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Job.tab.url}
                  key={Private_Routes.Job.tab.url}
                  element={
                    <PrivateMainSection>
                      <JobDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Pipeline.home.url}
                  key={Private_Routes.Pipeline.home.url}
                  element={
                    <PrivateMainSection>
                      <PipelineDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Pipeline.tab.url}
                  key={Private_Routes.Pipeline.tab.url}
                  element={
                    <PrivateMainSection>
                      <PipelineDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Settings.home.url}
                  key={Private_Routes.Settings.home.url}
                  element={
                    <UserSettingsWrapper
                      isProfessionalEditionEnabled={globalConfigManager.isProfessionalEditionEnabled}
                    />
                  }
                />
                <Route
                  path={Private_Routes.Settings.tab.url}
                  key={Private_Routes.Settings.tab.url}
                  element={
                    <UserSettingsWrapper
                      isProfessionalEditionEnabled={globalConfigManager.isProfessionalEditionEnabled}
                    />
                  }
                />
                <Route
                  path={Private_Routes.Project.home.url}
                  key={Private_Routes.Project.home.url}
                  element={
                    <PrivateMainSection>
                      <ProjectDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Project.tab.url}
                  key={Private_Routes.Project.tab.url}
                  element={
                    <PrivateMainSection>
                      <ProjectDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Fabric.home.url}
                  key={Private_Routes.Fabric.home.url}
                  element={
                    <PrivateMainSection>
                      <UpdateFabricPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Fabric.tab.url}
                  key={Private_Routes.Fabric.tab.url}
                  element={
                    <PrivateMainSection>
                      <UpdateFabricPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.Transpiler.url}
                  key={Private_Routes.Transpiler.url}
                  element={
                    <PrivateMainSection>
                      <TranspilerListingPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.TranspilerDetail.url}
                  key={Private_Routes.TranspilerDetail.url}
                  element={
                    <PrivateMainSection>
                      <TranspilerDetailPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.TranspilerImport.home.url}
                  key={Private_Routes.TranspilerImport.home.url}
                  element={
                    <PrivateMainSection>
                      <TranspilerImportPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.TranspilerImport.tab.url}
                  key={Private_Routes.TranspilerImport.tab.url}
                  element={
                    <PrivateMainSection>
                      <TranspilerImportPage />
                    </PrivateMainSection>
                  }
                />
                <Route
                  path={Private_Routes.IDE_Home.url}
                  key={Private_Routes.IDE_Home.url}
                  element={
                    <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
                      <IdeHome />
                    </PrivateMainSection>
                  }
                />
                <Route
                  key={Private_Routes.IDE.url}
                  path={Private_Routes.IDE.url}
                  element={
                    <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
                      <IDEPage />
                    </PrivateMainSection>
                  }
                />

                {globalConfigManager.isSQLProjectEnabled && (
                  <Route
                    key={Private_Routes.SQL_IDE.url}
                    path={Private_Routes.SQL_IDE.url}
                    element={
                      <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
                        <SqlPage />
                      </PrivateMainSection>
                    }
                  />
                )}
                <Route
                  key={Private_Routes.Onboarding.url}
                  path={Private_Routes.Onboarding.url}
                  element={
                    <PrivateMainSection wrapInLayout={true}>
                      {isProfessionalEditionEnabled ? (
                        <HomePage />
                      ) : shouldRedirectToOnboardingV2 ? (
                        <Onboarding />
                      ) : (
                        <OnboardingHome />
                      )}
                    </PrivateMainSection>
                  }
                />
                <Route
                  key={Private_Routes.Apps.home.url}
                  path={Private_Routes.Apps.home.url}
                  element={
                    <PrivateMainSection>
                      <AppListPage />
                    </PrivateMainSection>
                  }
                />
                {isDeploymentUXEnabled(user.user) && (
                  <Route
                    key={Private_Routes.Deployment.url}
                    path={Private_Routes.Deployment.url}
                    element={
                      <PrivateMainSection>
                        <DeploymentPage />
                      </PrivateMainSection>
                    }
                  />
                )}
              </>
            )}

            {defaultRoute}
          </Route>

          {defaultRoute}
        </SentryRoutes>
      </HistoryRouter>
    </>
  );
};

const OverflowContainer = styled(Stack)`
  overflow-y: auto;
`;
